<template>
    <div class="panel-graph-wrap" ref="panelGraphEl">
        <div class="panel-graph">
            <PanelCountBox
                v-for="(item, index) in props.data.Cards"
                :key="index"
                :value="item.PanelMainInfo ? Number(getDynamicValue(item, item.PanelMainInfo)) || 0 : 0"
                :unit="item.ChangeValueUnit ?? ''"
                :title="item.MainTitle ?? ''"
                :subTitle="item.SubTitle ?? ''"
                :color="item.Color ?? ''"
                :width="computedPanelBoxWidth"
                :shouldAnimate="animateTrigger"
            />
        </div>
        <p class="panel-graph-desc graph-1--regular" v-dompurify-html="props.data.FootNote"></p>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import PanelCountBox from './PanelCountBox.vue';
import { useThrottleFn } from '@common/common/composables/useThrottleFn';
import type { PanelGraph } from '@common/openapi-client/openapi';
import { getDynamicValue } from '@common/common/utils/typeUtils';

const props = defineProps<{
    data: PanelGraph;
    isAdmin: boolean;
}>();

const computedPanelBoxWidth = computed(() => {
    const cardLength = props.data.Cards?.length ?? 1;
    return `calc(100% / ${cardLength} - ${(cardLength - 1) / cardLength}rem)`;
});

const animateTrigger = ref(false);
const panelGraphEl = ref<HTMLDivElement>();

const startAnimation = () => {
    animateTrigger.value = false;
    requestAnimationFrame(() => {
        animateTrigger.value = true;
    });
};

const rawScrollHandler = () => {
    const panelTop = panelGraphEl.value?.getBoundingClientRect().top ?? 0;
    const triggerPoint = window.innerHeight * 0.85;

    if (panelTop < triggerPoint) {
        startAnimation();

        const scrollParentSelector = props.isAdmin ? '.mobile-screen' : '.wrapper';
        document.querySelector(scrollParentSelector)?.removeEventListener('scroll', throttledScrollHandler);
    }
};

const throttledScrollHandler = useThrottleFn(rawScrollHandler, 150);

onMounted(() => {
    const scrollParentSelector = props.isAdmin ? '.mobile-screen' : '.wrapper';
    document.querySelector(scrollParentSelector)?.addEventListener('scroll', throttledScrollHandler);
});

onUnmounted(() => {
    const scrollParentSelector = props.isAdmin ? '.mobile-screen' : '.wrapper';
    document.querySelector(scrollParentSelector)?.removeEventListener('scroll', throttledScrollHandler);
});
</script>

<style scoped lang="scss">
.panel-graph {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;

    & + .panel-graph {
        margin-top: 1.8rem;
    }
}

.panel-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 1rem;
    background-color: $grey-1;
    border-radius: 1rem;

    .box-title {
        font-weight: 400;
        font-size: 1.2rem;
        line-height: 150%;
        text-align: center;
        vertical-align: middle;
    }

    .change-value {
        display: flex;
        flex-direction: row;
        align-items: flex-end;
        justify-content: center;

        .change-value-text {
            font-weight: 700;
            font-size: 2.8rem;
            line-height: 170%;
            text-align: center;
            vertical-align: middle;
        }

        .change-value-unit {
            padding-bottom: 0.8rem;
            color: $grey-7;
        }
    }

    .change-percentage {
        color: $grey-7;
    }
}

.panel-graph-desc {
    padding-top: 1rem;
    line-height: 150%;
    color: $grey-6;
    word-break: break-all;
}
</style>
